import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { TranslocoService } from '@jsverse/transloco';
import { environment } from '../../../environments/environment';
import {
  AuthState,
  AuthUser,
  DecodedJwt,
  LoginError,
  LoginErrorCode,
  LoginRequest,
  LoginResponse,
} from './auth.models';
import { TokenStorageService } from './token-storage.service';
import { FormInstanceStateService } from '../../domains/report/services/form-instance-state.service';
import { MyProfileService } from '../../domains/report/services/my-profile.service';

function decodeJwt(token: string): DecodedJwt | null {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * El backend responde 429 cuando corta por fuerza bruta tras varios intentos
 * fallidos, así que ese caso no se puede mezclar con el 401 de credenciales.
 */
function loginErrorCode(err: unknown): LoginErrorCode {
  switch ((err as { status?: number } | null)?.status) {
    case 0:
      return 'noConnection';
    case 400:
    case 401:
      return 'invalidCredentials';
    case 429:
      return 'tooManyAttempts';
    default:
      return 'generic';
  }
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly api = `${environment.apiUrl}/auth`;
  private state$ = new BehaviorSubject<AuthState>({
    isAuthenticated: false,
    user: null,
    accessToken: null,
  });

  readonly authState$ = this.state$.asObservable();
  readonly isAuthenticated$ = this.authState$.pipe(map((s) => s.isAuthenticated));
  readonly user$ = this.authState$.pipe(map((s) => s.user));

  private formInstanceStateService = inject(FormInstanceStateService);
  private myProfileService = inject(MyProfileService);
  private translocoService = inject(TranslocoService);

  constructor(
    private http: HttpClient,
    private storage: TokenStorageService,
  ) {
    // Rehydrate if there is a valid token
    const token = this.storage.getAccessToken();
    if (token) {
      const dec = decodeJwt(token);
      const notExpired = !dec?.exp || dec.exp * 1000 > Date.now();
      if (notExpired) {
        const user: AuthUser = { id: dec?.sub ?? 'unknown', username: dec?.sub ?? 'unknown' };
        if (this.storage.getRemember()) this.storage.setRemember(true);
        this.state$.next({ isAuthenticated: true, user, accessToken: token });
      } else {
        this.storage.clear();
      }
    }
  }

  login(req: LoginRequest): Observable<AuthUser> {
    this.storage.setRemember(!!req.remember);
    return this.http
      .post<LoginResponse>(`${this.api}/login`, {
        username: req.username,
        password: req.password,
      })
      .pipe(
        tap(({ token }) => this.storage.saveAccessToken(token)),
        map(({ token, username }) => {
          const dec = decodeJwt(token);
          const user: AuthUser = { id: dec?.sub ?? username, username };
          this.state$.next({ isAuthenticated: true, user, accessToken: token });
          return user;
        }),
        catchError((err: unknown) => {
          const code = loginErrorCode(err);
          const msg = this.translocoService.translate(`login.errors.${code}`);
          return throwError(() => new LoginError(code, msg));
        }),
      );
  }

  /** Calls the backend and clears the local session regardless. */
  logout$() {
    const token = this.getAccessToken();
    const headers = token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : undefined;

    return this.http.post<void>(`${this.api}/logout`, {}, { headers }).pipe(
      catchError(() => of(void 0)),
      finalize(() => {
        this.storage.clear();
        this.formInstanceStateService.clearSelectedFormInstanceId();
        // MyProfileService es de raíz, así que su estado sobreviviría al
        // logout: sin esto el siguiente usuario ve la foto y el nombre del
        // anterior. Se limpia aquí y no en el botón de salir para cubrir
        // también la salida por 401 del interceptor.
        this.myProfileService.reset();
        this.state$.next({ isAuthenticated: false, user: null, accessToken: null });
      }),
    );
  }

  /** "Fire and forget" version */
  logout = () => this.logout$().subscribe();

  getAccessToken(): string | null {
    return this.state$.value.accessToken ?? this.storage.getAccessToken();
  }
}
